import React, { useState, useEffect } from 'react';
import axios from '../utils/authInterceptor';
import TappyLabsBranding from './TappyLabsBranding';
import ToastNotification from './ToastNotification';

const EssayStatus = ({ onClose }) => {
  const [essays, setEssays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEssay, setSelectedEssay] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  // Load teacher's essays on component mount
  useEffect(() => {
    loadEssays();
  }, []);

  const loadEssays = async () => {
    try {
      setLoading(true);
      console.log('Loading teacher essays...');
      const response = await axios.get('/api/essays/my-essays');
      
      if (response.data.success) {
        setEssays(response.data.essays);
        console.log('Loaded essays:', response.data.essays);
      } else {
        setError(response.data.error || 'Failed to load essays');
      }
    } catch (error) {
      console.error('Error loading essays:', error);
      setError('Failed to load essays');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: '', type: '' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'uploaded': return TappyLabsBranding.colors.secondary.ecosystemGreen;
      case 'queued': return TappyLabsBranding.colors.primary.confidenceFishGold;
      case 'processing': return TappyLabsBranding.colors.primary.learningAquariumBlue;
      case 'completed': return TappyLabsBranding.colors.secondary.breakthroughPurple;
      case 'failed': return TappyLabsBranding.colors.semantic.error;
      default: return TappyLabsBranding.colors.semantic.neutral;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploaded': return '📤';
      case 'queued': return '⏳';
      case 'processing': return '🔄';
      case 'completed': return '✅';
      case 'failed': return '❌';
      default: return '❓';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const handleViewResults = (essay) => {
    setSelectedEssay(essay);
    setShowResults(true);
  };

  const handleDownloadResults = async (essay) => {
    try {
      console.log('Downloading results for essay:', essay.id);
      const response = await axios.get(`/api/essays/${essay.id}/download-results`);
      
      if (response.data.success) {
        // Create download link and trigger download
        const link = document.createElement('a');
        link.href = response.data.download_url;
        link.download = response.data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        showToast('Download started successfully!', 'success');
      } else {
        showToast(`Download failed: ${response.data.error}`, 'error');
      }
    } catch (error) {
      console.error('Download error:', error);
      showToast('Download failed', 'error');
    }
  };

  return (
    <>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: TappyLabsBranding.colors.transparency.overlay,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(10px)'
      }}>
        {/* Modal Container */}
        <div style={{
          ...TappyLabsBranding.components.glassCard,
          maxWidth: '900px',
          width: '90%',
          maxHeight: '80vh',
          padding: '32px',
          position: 'relative',
          overflow: 'auto'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: `1px solid ${TappyLabsBranding.colors.transparency.medium}`
          }}>
            <h2 style={{
              margin: 0,
              fontFamily: TappyLabsBranding.typography.fonts.headers,
              fontSize: TappyLabsBranding.typography.scale['2xl'],
              fontWeight: TappyLabsBranding.typography.weights.semibold,
              color: TappyLabsBranding.colors.primary.deepOcean,
              background: TappyLabsBranding.colors.gradients.aquariumDepth,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              📊 My Essays & Results
            </h2>
            
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: TappyLabsBranding.colors.semantic.neutral,
                padding: '8px',
                borderRadius: '8px',
                transition: `all ${TappyLabsBranding.animations.durations.normal} ${TappyLabsBranding.animations.easing.ease}`
              }}
              onMouseEnter={(e) => {
                e.target.style.background = TappyLabsBranding.colors.transparency.subtle;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
              }}
            >
              ×
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px' }}>
              <div style={{
                fontSize: '48px',
                marginBottom: '16px',
                animation: `${TappyLabsBranding.animations.keyframes.float} 2s ease-in-out infinite`
              }}>
                🌊
              </div>
              <p style={{
                fontFamily: TappyLabsBranding.typography.fonts.body,
                fontSize: TappyLabsBranding.typography.scale.lg,
                color: TappyLabsBranding.colors.semantic.neutral
              }}>
                Loading your essays...
              </p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '48px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
              <p style={{
                fontFamily: TappyLabsBranding.typography.fonts.body,
                fontSize: TappyLabsBranding.typography.scale.lg,
                color: TappyLabsBranding.colors.semantic.error
              }}>
                {error}
              </p>
              <button
                onClick={loadEssays}
                style={{
                  ...TappyLabsBranding.components.buttons.primary,
                  marginTop: '16px'
                }}
              >
                Try Again
              </button>
            </div>
          ) : essays.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
              <p style={{
                fontFamily: TappyLabsBranding.typography.fonts.body,
                fontSize: TappyLabsBranding.typography.scale.lg,
                color: TappyLabsBranding.colors.semantic.neutral,
                marginBottom: '16px'
              }}>
                No essays uploaded yet
              </p>
              <p style={{
                fontFamily: TappyLabsBranding.typography.fonts.body,
                fontSize: TappyLabsBranding.typography.scale.base,
                color: TappyLabsBranding.colors.semantic.neutral
              }}>
                Upload your first essay to get started!
              </p>
            </div>
          ) : (
            /* Essays List */
            <div>
              <div style={{
                display: 'grid',
                gap: '16px'
              }}>
                {essays.map((essay) => (
                  <div
                    key={essay.id}
                    style={{
                      ...TappyLabsBranding.components.glassCard,
                      padding: '20px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '20px', marginRight: '8px' }}>📄</span>
                        <h3 style={{
                          margin: 0,
                          fontFamily: TappyLabsBranding.typography.fonts.body,
                          fontSize: TappyLabsBranding.typography.scale.lg,
                          fontWeight: TappyLabsBranding.typography.weights.semibold,
                          color: TappyLabsBranding.colors.primary.deepOcean
                        }}>
                          {essay.original_filename}
                        </h3>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '4px 8px',
                          background: getStatusColor(essay.processing_status) + '20',
                          border: `1px solid ${getStatusColor(essay.processing_status)}40`,
                          borderRadius: '12px',
                          fontSize: TappyLabsBranding.typography.scale.sm
                        }}>
                          <span style={{ marginRight: '4px' }}>{getStatusIcon(essay.processing_status)}</span>
                          <span style={{
                            color: getStatusColor(essay.processing_status),
                            fontWeight: TappyLabsBranding.typography.weights.medium,
                            textTransform: 'capitalize'
                          }}>
                            {essay.processing_status}
                          </span>
                        </div>
                        
                        <span style={{
                          fontSize: TappyLabsBranding.typography.scale.sm,
                          color: TappyLabsBranding.colors.semantic.neutral
                        }}>
                          {formatFileSize(essay.file_size)} • {formatDate(essay.upload_timestamp)}
                        </span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      {essay.processing_status === 'completed' && (
                        <>
                          {essay.has_results && (
                            <button
                              onClick={() => handleViewResults(essay)}
                              style={{
                                ...TappyLabsBranding.components.buttons.primary,
                                padding: '8px 16px',
                                fontSize: TappyLabsBranding.typography.scale.sm
                              }}
                            >
                              View Summary
                            </button>
                          )}
                          {essay.has_pdf_results && (
                            <button
                              onClick={() => handleDownloadResults(essay)}
                              style={{
                                ...TappyLabsBranding.components.buttons.secondary,
                                padding: '8px 16px',
                                fontSize: TappyLabsBranding.typography.scale.sm
                              }}
                            >
                              📄 Download Report
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results Modal */}
      {showResults && selectedEssay && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: TappyLabsBranding.colors.transparency.overlay,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          backdropFilter: 'blur(10px)'
        }}>
          <div style={{
            ...TappyLabsBranding.components.glassCard,
            maxWidth: '700px',
            width: '90%',
            maxHeight: '80vh',
            padding: '32px',
            position: 'relative',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h3 style={{
                margin: 0,
                fontFamily: TappyLabsBranding.typography.fonts.headers,
                fontSize: TappyLabsBranding.typography.scale.xl,
                color: TappyLabsBranding.colors.primary.deepOcean
              }}>
                Analysis Results: {selectedEssay.original_filename}
              </h3>
              <button
                onClick={() => setShowResults(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: TappyLabsBranding.colors.semantic.neutral
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{
              padding: '24px',
              background: TappyLabsBranding.colors.transparency.subtle,
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔬</div>
              <p style={{
                fontFamily: TappyLabsBranding.typography.fonts.body,
                fontSize: TappyLabsBranding.typography.scale.lg,
                color: TappyLabsBranding.colors.semantic.neutral
              }}>
                Detailed analysis results will appear here once your Lambda processing is complete.
              </p>
              <p style={{
                fontFamily: TappyLabsBranding.typography.fonts.body,
                fontSize: TappyLabsBranding.typography.scale.base,
                color: TappyLabsBranding.colors.semantic.neutral,
                marginTop: '16px'
              }}>
                Results include: Writing quality, grammar analysis, content assessment, and improvement suggestions.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      <ToastNotification
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
        autoClose={4000}
      />
    </>
  );
};

export default EssayStatus;